import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Upload, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function AdditionalImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; };
  const close = () => { reset(); onClose?.(); };

  const downloadSkippedCsv = (rows, originalName) => {
    if (!rows || !rows.length) return;
    // Preserve original CSV column order + prepend 'reason' and 'row' meta.
    const dataCols = Object.keys(rows[0]).filter((k) => k !== "row" && k !== "reason");
    const headers = ["row", "reason", ...dataCols];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    rows.forEach((r) => lines.push(headers.map((h) => escape(r[h])).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const base = (originalName || "import").replace(/\.csv$/i, "");
    link.href = url;
    link.download = `${base}_skipped.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a CSV file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/additional-compliances/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} record(s)${data.skipped_duplicates ? ` · ${data.skipped_duplicates} duplicate(s) skipped` : ""}${data.errors?.length ? ` · ${data.errors.length} error(s)` : ""}`);
        onDone?.();
      } else if (!data.errors?.length) {
        toast.info("Nothing to import.");
      } else {
        toast.error("No rows imported. See errors below.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">Import Additional Compliance from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV of Audit / CT / Other compliance records. Clients auto-linked from Client Master;
            duplicates (same Client + Type + Period) are skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
          <div className="font-medium text-slate-800 mb-1">Columns (compliance_type &amp; client_name required)</div>
          <div className="font-mono-num text-[11px] break-all">
            compliance_type, client_name, report_number, partner_name, person_1, reviewer, fees, invoice_status, jurisdiction, period, status, remarks
          </div>
          <a
            href={`${API_BASE}/additional-compliances/import/template.csv`}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-blue-700 hover:underline text-xs font-medium"
            data-testid="additional-import-template-link"
          >
            <Download className="w-3.5 h-3.5" /> Download CSV template
          </a>
        </div>

        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="additional-import-form">
          <label className="flex items-center justify-center gap-3 w-full rounded-lg border-2 border-dashed border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50 transition-colors py-8 cursor-pointer">
            <Upload className="w-5 h-5 text-slate-500" />
            <div className="text-sm">
              {file ? <span className="font-medium text-slate-800 font-mono-num">{file.name}</span> :
                <><span className="text-slate-700 font-medium">Choose CSV file</span><span className="text-slate-500"> or drag it here</span></>}
            </div>
            <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="additional-import-file" />
          </label>
          {result && (
            <div className="rounded-md border border-slate-200 bg-white p-3 text-sm space-y-1.5" data-testid="additional-import-result">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-4 h-4" />
                <span>Created <span className="font-mono-num font-medium">{result.created}</span> record(s)</span>
              </div>
              {typeof result.skipped_duplicates === "number" && result.skipped_duplicates > 0 && (
                <div className="text-amber-700 text-xs">Skipped <span className="font-mono-num font-medium">{result.skipped_duplicates}</span> duplicate(s)</div>
              )}
              {result.errors?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 text-red-700 mt-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>Errors on {result.errors.length} row(s):</span>
                  </div>
                  <ul className="max-h-40 overflow-auto mt-1 text-xs font-mono-num text-red-700 pl-6 list-disc">
                    {result.errors.map((er) => <li key={`err-${er.row}-${er.error}`}>Row {er.row}: {er.error}</li>)}
                  </ul>
                </div>
              )}
              {Array.isArray(result.skipped_rows) && result.skipped_rows.length > 0 && (
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => downloadSkippedCsv(result.skipped_rows, file?.name)}
                    data-testid="additional-download-skipped"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Download {result.skipped_rows.length} skipped row(s)
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>Close</Button>
            <Button type="submit" disabled={!file || uploading} data-testid="additional-import-submit">
              {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Import
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
